import { createFileRoute, redirect } from '@tanstack/react-router';
import { Check, Loader2 } from 'lucide-react';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import {
  usePortalMembership,
  type PortalCoveredService,
  type PortalMembership,
} from '@/hooks/portal/usePortalMemberships';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalBookVisitDialog } from '@/components/portal/PortalBookVisitDialog';
import { ApiError } from '@/lib/api/errors';
import { formatBilling, formatRenews, membershipStatusBadge } from '@/lib/portal/membershipDisplay';

export const Route = createFileRoute('/(portal)/portal/memberships/$id')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalMembershipDetailPage,
});

function PortalMembershipDetailPage() {
  const { id } = Route.useParams();
  const membership = usePortalMembership(id);
  const notFound = membership.error instanceof ApiError && membership.error.status === 404;

  const m = membership.data;
  const crumb = m
    ? m.planName?.trim()
      ? m.planName
      : `Membership ${m.membershipNumber}`
    : undefined;

  return (
    <PortalShell back={{ label: 'Memberships', to: '/portal/memberships', crumb }}>
      {membership.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : notFound ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This membership isn&apos;t available.
          </CardContent>
        </Card>
      ) : membership.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            We couldn&apos;t load this membership. Please try again.
          </CardContent>
        </Card>
      ) : (
        <MembershipDetail data={membership.data} />
      )}
    </PortalShell>
  );
}

function periodLabel(kind: string | null, qty: number | null): string {
  const period =
    kind === 'ANNUAL'
      ? 'year'
      : kind === 'CONTRACT_TERM'
        ? 'contract'
        : kind === 'LIFETIME'
          ? 'lifetime'
          : 'billing period';
  if (qty && qty > 0) return `${qty} per ${period}`;
  return `Included ${period === 'lifetime' ? 'for life' : `each ${period}`}`;
}

const STATUS_TONE: Record<string, PortalPillTone> = {
  ACTIVE: 'success',
  PENDING: 'accent',
  PAST_DUE: 'accent',
  SUSPENDED: 'accent',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
};

/** Annualized plan cost from the billing interval — the artifact's "Plan cost /
 * per year" tile. Null when the interval unit isn't one we can normalize. */
function annualCost(data: PortalMembership): number | null {
  // The unit can arrive singular or plural, any case ("MONTHS", "month", …).
  const unit = data.billingIntervalUnit?.toLowerCase() ?? '';
  const val = data.billingIntervalValue || 1;
  const perYear = unit.startsWith('month')
    ? 12 / val
    : unit.startsWith('week')
      ? 52 / val
      : unit.startsWith('year')
        ? 1 / val
        : unit.startsWith('quarter')
          ? 4 / val
          : unit.startsWith('day')
            ? 365 / val
            : null;
  if (!perYear) return null;
  return Math.round(data.billingAmount * perYear * 100) / 100;
}

function MembershipDetail({ data }: { data: PortalMembership }) {
  const badge = membershipStatusBadge(data.status);
  const tone = STATUS_TONE[data.status] ?? 'neutral';
  const planName = data.planName ?? `Membership ${data.membershipNumber}`;
  const memberSince = data.contractStartDate ?? data.activatedAt;
  const annual = annualCost(data);
  const cv = data.coveredVisits;

  // The metric-tile row — each tile renders only when its datum exists.
  const tiles: Array<{ label: string; value: string; sub: string }> = [
    ...(data.isComplimentary && !(data.billingAmount > 0)
      ? [
          {
            label: 'Plan cost',
            value: 'Free',
            sub: data.contractEndDate
              ? `Until ${formatDateOnly(data.contractEndDate)}`
              : 'Nothing is charged',
          },
        ]
      : annual != null
        ? [{ label: 'Plan cost', value: formatCurrency(annual), sub: 'Per year' }]
        : []),
    ...(cv
      ? [{ label: 'Visits left', value: String(cv.remaining), sub: `of ${cv.total} this term` }]
      : []),
    ...(data.nextBillingDate && !(data.isComplimentary && !(data.billingAmount > 0))
      ? [
          {
            label: 'Next charge',
            value: formatDateOnly(data.nextBillingDate),
            sub: formatBilling(data),
          },
        ]
      : []),
    {
      label: 'Auto-renew',
      value: data.autoRenew ? 'On' : 'Off',
      sub: data.autoRenew ? 'Renews automatically' : 'Will not renew',
    },
  ];
  const cols =
    tiles.length >= 4 ? 'sm:grid-cols-4' : tiles.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';

  const hasExtraDates = Boolean(
    data.currentPeriodEnd || data.contractEndDate || data.cancellationEffectiveDate,
  );

  return (
    <div className="space-y-4">
      {/* Head */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{planName}</h1>
          <p className="text-sm text-muted-foreground">
            {memberSince
              ? `Member since ${formatDateOnly(memberSince)}`
              : `#${data.membershipNumber}`}
          </p>
        </div>
        <PortalStatusPill label={badge.label} tone={tone} />
      </div>

      {/* Plan hero — ring + plan + renews + actions */}
      <Card>
        <CardContent className="flex flex-col gap-5 py-5 sm:flex-row sm:items-center">
          {cv ? <VisitsRing used={cv.used} total={cv.total} size={96} /> : null}
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="text-lg font-semibold">{planName}</p>
              <p className="text-sm text-muted-foreground">
                {formatBilling(data)}
                {formatRenews(data)}
              </p>
            </div>
            {cv && cv.remaining > 0 ? (
              <PortalBookVisitDialog
                trigger={
                  <Button variant="outline" size="sm" className="w-fit">
                    Book {cv.remaining === 1 ? 'my remaining visit' : 'a visit'}
                  </Button>
                }
              />
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Metric tiles */}
      {tiles.length > 0 ? (
        <div
          className={`grid grid-cols-2 divide-x divide-y divide-border overflow-hidden rounded-xl border border-border bg-card sm:divide-y-0 ${cols}`}
        >
          {tiles.map((t) => (
            <StatTile key={t.label} label={t.label} value={t.value} sub={t.sub} />
          ))}
        </div>
      ) : null}

      {/* What's included */}
      {data.coveredServices.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              What&apos;s included
            </p>
            {data.coveredServices.map((c, i) => (
              <CoveredRow key={i} covered={c} />
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Plan details — edge dates that don't belong in the tiles */}
      <MembershipBenefits data={data} />

      {hasExtraDates ? (
        <Card>
          <CardContent className="space-y-1 py-4 text-sm">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Plan details
            </p>
            <Row label="Membership #" value={data.membershipNumber} />
            {data.currentPeriodEnd ? (
              <Row label="Current period ends" value={formatDateOnly(data.currentPeriodEnd)} />
            ) : null}
            {data.contractEndDate ? (
              <Row label="Contract ends" value={formatDateOnly(data.contractEndDate)} />
            ) : null}
            {data.cancellationEffectiveDate ? (
              <Row label="Cancels on" value={formatDateOnly(data.cancellationEffectiveDate)} />
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export function MembershipBenefits({ data }: { data: PortalMembership }) {
  const equipment = data.coveredEquipment ?? [];
  const money = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.billingCurrency,
    }).format(value);
  return (
    <Card>
      <CardContent className="space-y-5 py-5">
        <section className="space-y-3" aria-label="Covered equipment">
          <h2 className="text-sm font-semibold">Covered equipment</h2>
          {equipment.length ? (
            <ul className="divide-y divide-border">
              {equipment.map((unit) => (
                <li
                  key={unit.id}
                  className="space-y-1 py-3 first:pt-0 last:pb-0 text-sm break-words"
                >
                  <p className="font-medium">{unit.name}</p>
                  {unit.make || unit.model ? (
                    <p className="text-muted-foreground">
                      {[unit.make, unit.model].filter(Boolean).join(' ')}
                    </p>
                  ) : null}
                  {unit.serialNumber ? (
                    <p className="text-muted-foreground">Serial: {unit.serialNumber}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No individual equipment is recorded for this membership.
            </p>
          )}
        </section>
        <section className="space-y-3 border-t border-border pt-5" aria-label="Member discounts">
          <h2 className="text-sm font-semibold">Member discounts</h2>
          {data.discounts?.length ? (
            <>
              <ul className="space-y-2 text-sm">
                {data.discounts.map((discount, index) => (
                  <li key={index}>
                    {discount.type === 'PERCENT'
                      ? `${discount.value}% off`
                      : discount.type === 'FLAT'
                        ? `${money(discount.value)} off`
                        : `Member price: ${money(discount.value)}`}{' '}
                    {discount.scope}
                    {discount.hasExclusions ? ' (exclusions apply)' : ''}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Rates apply to qualifying items under your membership terms and are not added
                together. Your estimate or invoice shows the savings applied.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {data.discounts == null
                ? 'Discount details are not recorded for this membership.'
                : 'No plan discounts are recorded for this membership.'}
            </p>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function StatTile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
      <p className="truncate text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function CoveredRow({ covered }: { covered: PortalCoveredService }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Check className="mt-0.5 size-4 shrink-0 text-portal-accent" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{covered.name}</p>
        <p className="text-xs text-muted-foreground">
          {periodLabel(covered.entitlementPeriodType, covered.quantityPerPeriod)}
        </p>
      </div>
    </div>
  );
}

/** Donut of used/total covered visits — sized for the plan hero (or compact). */
function VisitsRing({ used, total, size = 48 }: { used: number; total: number; size?: number }) {
  const pct = total > 0 ? Math.min(1, used / total) : 0;
  const r = 16;
  const c = 2 * Math.PI * r;
  const big = size >= 80;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 40 40" className="-rotate-90" style={{ width: size, height: size }}>
        <circle cx="20" cy="20" r={r} fill="none" strokeWidth="4" className="stroke-muted" />
        <circle
          cx="20"
          cy="20"
          r={r}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          className="stroke-portal-accent"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none">
        <span className={big ? 'text-lg font-semibold' : 'text-xs font-semibold'}>
          {used}/{total}
        </span>
        {big ? <span className="mt-0.5 text-[9px] text-muted-foreground">visits used</span> : null}
      </span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
